import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, Upload, X, Save, Search, Image as ImageIcon } from "lucide-react";

const UNITS = ["kg", "caisse", "unité", "piece", "botte"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("Tous");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/products");
      setProducts(data);
      setSelectedIds(new Set());
    } catch { toast.error("Erreur"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const categories = ["Tous", ...Array.from(new Set(products.flatMap((p) => p.categories?.length ? p.categories : (p.category ? [p.category] : [])))).sort()];
  const filtered = products.filter((p) => {
    const prodCats = p.categories?.length ? p.categories : (p.category ? [p.category] : []);
    const matchCat = filterCat === "Tous" || prodCats.includes(filterCat);
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted} créés, ${data.updated} mis à jour${data.errors.length ? `, ${data.errors.length} erreurs` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur import");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Produit supprimé");
      load();
    } catch { toast.error("Erreur"); }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} produit(s) ?`)) return;
    try {
      const { data } = await api.post("/admin/products/bulk-delete", { ids });
      toast.success(`${data.deleted} produit(s) supprimé(s)`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="ff-overline">Catalogue</p>
          <h1 className="font-serif text-3xl mt-2">Produits ({products.length})</h1>
        </div>
        <div className="flex gap-3">
          <label className="ff-btn-secondary cursor-pointer" data-testid="admin-import-excel-button">
            <Upload className="w-4 h-4" /> Importer Excel / CSV
            <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onUpload} className="hidden" data-testid="admin-import-excel-input" />
          </label>
          <button onClick={() => setEditing({})} className="ff-btn-primary" data-testid="admin-new-product-button">
            <Plus className="w-4 h-4" /> Nouveau produit
          </button>
        </div>
      </div>

      <div className="mt-4 text-xs text-[#8A9A8E] bg-[#F2EFEB] p-3 border border-[#E5E3DC]">
        <strong>Import Excel ou CSV :</strong> colonnes attendues — <code>nom</code>, <code>prix</code>, <code>catégorie</code> (séparez par virgule pour plusieurs : « Fruits, Réduction »), <code>type</code> (kg / caisse / unité / piece / botte), <code>image</code> (URL, optionnel), <code>origine</code> (ex. Provence, optionnel), <code>description</code> (optionnel).
      </div>

      {/* Filters */}
      <div className="mt-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A8E]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un produit..."
            className="ff-input pl-10"
            data-testid="admin-products-search"
          />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="ff-input md:w-56" data-testid="admin-products-category-filter">
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="mt-8 text-[#8A9A8E]">Chargement...</p>
      ) : (
        <>
          {selectedIds.size > 0 && (
            <div className="mt-4 bg-[#1A2B1E] text-white p-3 flex items-center justify-between" data-testid="admin-bulk-action-bar">
              <span className="text-sm">{selectedIds.size} produit(s) sélectionné(s)</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-sm px-3 py-1.5 border border-white/30 hover:bg-white/10"
                  data-testid="admin-bulk-clear"
                >
                  Désélectionner
                </button>
                <button
                  onClick={bulkDelete}
                  className="text-sm px-3 py-1.5 bg-[#D62828] hover:bg-[#9B1D1D] inline-flex items-center gap-1.5"
                  data-testid="admin-bulk-delete"
                >
                  <Trash2 className="w-4 h-4" /> Supprimer la sélection
                </button>
              </div>
            </div>
          )}
        <div className="mt-4 bg-white border border-[#E5E3DC] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFEB] text-left">
              <tr>
                <th className="p-3 w-10">
                  <input
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every((p) => selectedIds.has(p.id))}
                    onChange={(e) => {
                      const next = new Set(selectedIds);
                      if (e.target.checked) filtered.forEach((p) => next.add(p.id));
                      else filtered.forEach((p) => next.delete(p.id));
                      setSelectedIds(next);
                    }}
                    data-testid="admin-select-all"
                    aria-label="Tout sélectionner"
                  />
                </th>
                <th className="p-3">Image</th>
                <th className="p-3">Nom</th>
                <th className="p-3">Catégorie</th>
                <th className="p-3">Origine</th>
                <th className="p-3">Unité</th>
                <th className="p-3 text-right">Prix</th>
                <th className="p-3 text-center">Disponible</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className={`border-t border-[#F2EFEB] ${selectedIds.has(p.id) ? "bg-[#2D6A4F]/5" : ""}`}
                  data-testid={`admin-product-row-${p.id}`}
                >
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`admin-select-product-${p.id}`}
                      aria-label={`Sélectionner ${p.name}`}
                    />
                  </td>
                  <td className="p-3"><img src={resolveImageUrl(p.image_url)} alt={p.name} className="w-12 h-12 object-cover bg-[#F2EFEB]" /></td>
                  <td className="p-3 font-medium">{p.name}</td>
                  <td className="p-3 text-[#4A5D4E]">
                    <div className="flex flex-wrap gap-1">
                      {(p.categories && p.categories.length ? p.categories : [p.category]).filter(Boolean).map((c) => (
                        <span key={c} className="text-[10px] px-1.5 py-0.5 bg-[#F2EFEB]">{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3 text-[#4A5D4E] text-xs">{p.origin || "—"}</td>
                  <td className="p-3 text-[#4A5D4E]">{p.unit}</td>
                  <td className="p-3 text-right">CHF {p.price.toFixed(2)}</td>
                  <td className="p-3 text-center">
                    <span className={`px-2 py-0.5 text-xs ${p.available ? "bg-[#2D6A4F]/15 text-[#2D6A4F]" : "bg-[#D62828]/15 text-[#D62828]"}`}>
                      {p.available ? "Oui" : "Non"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => setEditing(p)} className="p-2 hover:bg-[#F2EFEB]" data-testid={`admin-edit-product-${p.id}`}><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => remove(p.id)} className="p-2 hover:bg-[#F2EFEB] text-[#D62828]" data-testid={`admin-delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {editing !== null && (
        <ProductModal product={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isNew = !product.id;
  const initialCategories = Array.isArray(product.categories) && product.categories.length
    ? product.categories
    : product.category ? [product.category] : [];
  const [form, setForm] = useState({
    name: product.name || "",
    price: product.price ?? 0,
    categories: initialCategories,
    unit: product.unit || "kg",
    image_url: product.image_url || "",
    description: product.description || "",
    origin: product.origin || "",
    stock_unlimited: product.stock_unlimited ?? true,
    stock: product.stock ?? 0,
    available: product.available ?? true,
  });
  const [allCategories, setAllCategories] = useState([]);
  const [newCatInput, setNewCatInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/admin/categories")
      .then(({ data }) => setAllCategories(data.map((c) => c.name)))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };
  const addCategory = (name) => {
    const n = name.trim();
    if (!n) return;
    setForm((f) => ({ ...f, categories: f.categories.includes(n) ? f.categories : [...f.categories, n] }));
    setNewCatInput("");
  };
  const removeCategory = (name) => {
    setForm((f) => ({ ...f, categories: f.categories.filter((c) => c !== name) }));
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        stock: form.stock_unlimited ? 0 : Number(form.stock),
        category: form.categories[0] || "",
        categories: form.categories,
      };
      if (isNew) {
        await api.post("/admin/products", payload);
        toast.success("Produit créé");
      } else {
        await api.patch(`/admin/products/${product.id}`, payload);
        toast.success("Produit mis à jour");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E3DC] flex items-center justify-between">
          <h2 className="font-serif text-2xl">{isNew ? "Nouveau produit" : "Modifier le produit"}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F2EFEB]"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nom" value={form.name} onChange={set("name")} testid="product-form-name" />
          <div className="grid grid-cols-2 gap-4">
            <Field label="Prix (CHF)" type="number" step="0.01" value={form.price} onChange={set("price")} testid="product-form-price" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Unité</label>
              <select value={form.unit} onChange={set("unit")} className="ff-input mt-1" data-testid="product-form-unit">
                {UNITS.map((u) => <option key={u}>{u}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Catégories</label>
            <p className="text-[10px] text-[#8A9A8E] mt-0.5">Un produit peut appartenir à plusieurs catégories (ex. « Fruits » + « Réduction »).</p>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="product-form-categories-chips">
              {form.categories.length === 0 && (
                <span className="text-xs text-[#8A9A8E]">Aucune catégorie sélectionnée.</span>
              )}
              {form.categories.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 px-2 py-1 bg-[#2D6A4F] text-white text-xs" data-testid={`product-form-cat-chip-${c}`}>
                  {c}
                  <button type="button" onClick={() => removeCategory(c)} className="hover:text-[#F4A261]"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="mt-2 flex gap-2 flex-wrap">
              {allCategories.filter((c) => !form.categories.includes(c)).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => addCategory(c)}
                  className="text-xs px-2 py-1 border border-[#E5E3DC] hover:bg-[#F2EFEB]"
                  data-testid={`product-form-add-cat-${c}`}
                >
                  + {c}
                </button>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <input
                type="text"
                value={newCatInput}
                onChange={(e) => setNewCatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(newCatInput); }}}
                placeholder="Nouvelle catégorie..."
                className="ff-input flex-1"
                data-testid="product-form-new-category-input"
              />
              <button
                type="button"
                onClick={() => addCategory(newCatInput)}
                className="ff-btn-secondary text-sm"
                data-testid="product-form-add-new-category"
              >
                Ajouter
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Origine (ex. Provence, Genève, Espagne)" value={form.origin} onChange={set("origin")} testid="product-form-origin" placeholder="Provence" />
            <div>
              <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Stock</label>
              <label className="flex items-center gap-2 mt-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.stock_unlimited}
                  onChange={(e) => setForm((f) => ({ ...f, stock_unlimited: e.target.checked }))}
                  data-testid="product-form-stock-unlimited"
                />
                <span>Stock illimité</span>
              </label>
              {!form.stock_unlimited && (
                <input
                  type="number"
                  min="0"
                  value={form.stock}
                  onChange={set("stock")}
                  className="ff-input mt-2"
                  placeholder="Quantité en stock"
                  data-testid="product-form-stock"
                />
              )}
            </div>
          </div>
          <Field label="URL de l'image" value={form.image_url} onChange={set("image_url")} testid="product-form-image" placeholder="https://... ou upload ci-dessous" />
          <div className="flex items-center gap-3">
            <label className="ff-btn-secondary cursor-pointer text-sm" data-testid="product-form-upload-button">
              <Upload className="w-4 h-4" /> {uploading ? "Upload..." : "Téléverser une image"}
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                disabled={uploading}
                onChange={uploadImage}
                className="hidden"
                data-testid="product-form-upload-input"
              />
            </label>
            <span className="text-xs text-[#8A9A8E]">PNG / JPG / WEBP · 5 Mo max</span>
          </div>
          {form.image_url && (
            <div className="border border-[#E5E3DC] p-2 inline-block" data-testid="product-form-image-preview">
              <img src={resolveImageUrl(form.image_url)} alt="Aperçu" className="w-32 h-32 object-cover bg-[#F2EFEB]" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Description</label>
            <textarea value={form.description} onChange={set("description")} rows={2} className="ff-input mt-1" data-testid="product-form-description" />
          </div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={form.available} onChange={set("available")} data-testid="product-form-available" />
            <span>Disponible à la vente</span>
          </label>
        </div>
        <div className="p-6 border-t border-[#E5E3DC] flex justify-end gap-3">
          <button onClick={onClose} className="ff-btn-secondary">Annuler</button>
          <button onClick={save} disabled={saving} className="ff-btn-primary" data-testid="product-form-save">
            <Save className="w-4 h-4" /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, ...rest }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">{label}</label>
      <input className="ff-input mt-1" data-testid={testid} {...rest} />
    </div>
  );
}
